/**
 * §8.5 Academy Preview — 6 highlights.  Explicit note that the full
 * Academy lives inside the product; this is a preview only.
 */

import React from 'react'
import {
  Bot, LineChart, Wand2, Building2, ShieldCheck, TrendingUp, GraduationCap, Sparkles, ArrowRight, Lock,
} from 'lucide-react'
import {
  Container, Eyebrow, SectionHeading, GradientButton, GhostButton,
} from '@/components/landing/design-system'
import { ACADEMY_HIGHLIGHTS } from '../data'
import { TONE } from '../_shared'

const ICONS = { Bot, LineChart, Wand2, Building2, ShieldCheck, TrendingUp }

export default function Academy() {
  return (
    <section id="academy" className="relative py-24 md:py-28 scroll-mt-24">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow tone="green">Academy Preview</Eyebrow>
          <SectionHeading align="center"
            title={<>The learning experience{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">inside Kalki Ai Cloud.</span></>}
            subtitle="A quick preview of the six pillars.  The full Academy — courses, tracks, certificates and progress — lives inside the product itself once you subscribe."
          />
        </div>

        {/* preview note */}
        <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.05] px-5 md:px-6 py-4 backdrop-blur-xl flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
          <GraduationCap className="h-4 w-4 text-emerald-400 shrink-0" />
          <p className="text-sm text-zinc-100 leading-relaxed">
            <span className="font-semibold text-white">This is a preview.</span>{' '}
            The full Academy — with courses, video lessons, certificates and per-user progress — is available inside every Kalki Ai Cloud workspace.
          </p>
        </div>

        {/* highlights grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {ACADEMY_HIGHLIGHTS.map((h) => {
            const tone = TONE[h.tone] || TONE.orange
            const Icon = ICONS[h.iconKey] || Sparkles
            return (
              <div key={h.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl transition-all hover:border-white/20 hover:bg-white/[0.03] overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                <div className="relative flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                      <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                    </span>
                    <span className="inline-flex items-center gap-1 text-[9.5px] font-mono uppercase tracking-wider text-zinc-500">
                      <Lock className="h-3 w-3" /> in-product
                    </span>
                  </div>
                  <div>
                    <h4 className="text-[15px] font-semibold text-white leading-tight">{h.label}</h4>
                    <p className="text-[13px] text-zinc-400 leading-relaxed mt-1.5">{h.body}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* CTA row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <GradientButton href="/pricing#pricing-plans" size="lg">
            <Sparkles className="h-4 w-4" /> Unlock full Academy
          </GradientButton>
          <GhostButton href="#videos" size="lg" trailingIcon>
            Watch video previews <ArrowRight className="h-3 w-3" />
          </GhostButton>
        </div>
      </Container>
    </section>
  )
}
